import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "./components/DashboardLayout";
import StandaloneSqafLayout from "./components/StandaloneSqafLayout";
import StandaloneProgramLayout from "./components/StandaloneProgramLayout";
import StandalonePgiLayout from "./components/StandalonePgiLayout";
import StandaloneGerLayout from "./components/StandaloneGerLayout";
import StandaloneNipunLayout from "./components/StandaloneNipunLayout";
import StandalonePatLayout from "./components/StandalonePatLayout";
import ProgramDashboard from "./pages/ProgramDashboard";
import PgiDashboard from "./pages/PgiDashboard";
import GerDashboard from "./pages/GerDashboard";
import NipunDashboard from "./pages/NipunDashboard";
import PatDashboard from "./pages/PatDashboard";
import StateOverview from "./pages/StateOverview";
import SchoolHealthIndex from "./pages/SchoolHealthIndex";
import DistrictDetail from "./pages/DistrictDetail";
import BlockDetail from "./pages/BlockDetail";
import SchoolDetail from "./pages/SchoolDetail";
import DataImport from "./pages/DataImport";
import AadhaarDashboard from "./pages/AadhaarDashboard";
import TeacherDashboard from "./pages/TeacherDashboard";
import InfrastructureDashboard from "./pages/InfrastructureDashboard";
import EnrolmentDashboard from "./pages/EnrolmentDashboard";
import DropboxDashboard from "./pages/DropboxDashboard";
import DataEntryDashboard from "./pages/DataEntryDashboard";
import AgeEnrolmentDashboard from "./pages/AgeEnrolmentDashboard";
import CTTeacherDashboard from "./pages/CTTeacherDashboard";
import APAARDashboard from "./pages/APAARDashboard";
import ClassroomsToiletsDashboard from "./pages/ClassroomsToiletsDashboard";
import SchoolTypeBifurcationDashboard from "./pages/SchoolTypeBifurcationDashboard";
import ManagementwiseBifurcationDashboard from "./pages/ManagementwiseBifurcationDashboard";
import ExecutiveDashboard from "./pages/ExecutiveDashboard";
import SqafDashboard from "./pages/SqafDashboard";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";
import UserManagement from "./pages/UserManagement";
import { Toaster } from "@/components/ui/sonner";
import { getBackendOrigin } from "@/lib/backend";
import AppBootScreen from "@/components/AppBootScreen";
import ScrollToTop from "@/components/ScrollToTop";
import {
  clearStoredSession,
  ensureAuthenticated,
  performAutoLogin,
} from "@/lib/auth";

// Avoid "infinite loading" if a request hangs (axios default timeout is 0 = no timeout).
// This is especially common when the frontend is opened via a LAN URL but the backend
// is still configured as localhost, or if a backend process is not reachable.
axios.defaults.timeout = 20000; // 20s

const IS_GITHUB_PAGES = typeof window !== "undefined" && window.location.hostname.endsWith("github.io");
const ROUTER_BASENAME = process.env.PUBLIC_URL || "/";

// Protected Route — auto-login when entering dashboards; landing page stays public at /
const ProtectedRoute = ({ children, user, setUser, allowedRoles }) => {
  const [authReady, setAuthReady] = useState(!!user);
  const [resolvedUser, setResolvedUser] = useState(user);

  useEffect(() => {
    setResolvedUser(user);
    if (user) setAuthReady(true);
  }, [user]);

  useEffect(() => {
    if (user) return;
    let cancelled = false;
    (async () => {
      try {
        const authUser = await ensureAuthenticated();
        if (!cancelled && authUser) {
          setResolvedUser(authUser);
          setUser(authUser);
        }
      } catch (error) {
        console.warn("Protected route auth failed:", error?.response?.data?.detail || error.message);
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, setUser]);

  const activeUser = user || resolvedUser;

  if (!authReady) {
    return <AppBootScreen subtitle="Signing in and loading dashboard…" />;
  }
  if (!activeUser) {
    return <Navigate to="/" replace />;
  }
  if (allowedRoles && !allowedRoles.includes(activeUser.role)) {
    return <Navigate to="/executive-dashboard" replace />;
  }
  return children;
};

const AdminOnly = ({ user, children }) =>
  user?.role === "admin" ? children : <Navigate to="/executive-dashboard" replace />;

function LoginRoute({ user, setUser }) {
  const location = useLocation();
  const redirectTo = location.state?.from || "/executive-dashboard";
  return user ? <Navigate to={redirectTo} replace /> : <LoginPage onLogin={setUser} />;
}

function SqaafLegacyRedirect() {
  const location = useLocation();
  return <Navigate to={`/sqaaf-dashboard${location.search}`} replace />;
}

function AppRoutes({ user, setUser, onLogout, useHashRouter }) {
  const routes = (
      <Routes>
        <Route path="/" element={<LandingPage onLogin={setUser} />} />
        <Route path="/login" element={<LoginRoute user={user} setUser={setUser} />} />
        <Route
          path="/sqaaf"
          element={
            <ProtectedRoute user={user} setUser={setUser}>
              <SqaafLegacyRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          element={
            <ProtectedRoute user={user} setUser={setUser}>
              <StandaloneSqafLayout user={user} onLogout={onLogout} />
            </ProtectedRoute>
          }
        >
          <Route path="sqaaf-dashboard" element={<SqafDashboard />} />
        </Route>
        <Route
          element={
            <ProtectedRoute user={user} setUser={setUser}>
              <StandalonePgiLayout user={user} onLogout={onLogout} />
            </ProtectedRoute>
          }
        >
          <Route path="pgi-dashboard" element={<PgiDashboard />} />
        </Route>
        <Route
          element={
            <ProtectedRoute user={user} setUser={setUser}>
              <StandaloneGerLayout user={user} onLogout={onLogout} />
            </ProtectedRoute>
          }
        >
          <Route path="ger-dashboard" element={<GerDashboard />} />
        </Route>
        <Route
          element={
            <ProtectedRoute user={user} setUser={setUser}>
              <StandaloneNipunLayout user={user} onLogout={onLogout} />
            </ProtectedRoute>
          }
        >
          <Route path="nipun-dashboard" element={<NipunDashboard />} />
        </Route>
        <Route
          element={
            <ProtectedRoute user={user} setUser={setUser}>
              <StandalonePatLayout user={user} onLogout={onLogout} />
            </ProtectedRoute>
          }
        >
          <Route path="pat-dashboard" element={<PatDashboard />} />
        </Route>
        <Route
          element={
            <ProtectedRoute user={user} setUser={setUser}>
              <StandaloneProgramLayout user={user} onLogout={onLogout} />
            </ProtectedRoute>
          }
        >
          <Route path="nas-dashboard" element={<ProgramDashboard />} />
        </Route>
        <Route
          element={
            <ProtectedRoute user={user} setUser={setUser}>
              <DashboardLayout user={user} onLogout={onLogout} />
            </ProtectedRoute>
          }
        >
          <Route path="state-overview" element={<StateOverview />} />
          <Route path="health-index" element={<SchoolHealthIndex />} />
          <Route
            path="data-import"
            element={
              <AdminOnly user={user}>
                <DataImport />
              </AdminOnly>
            }
          />
          <Route path="aadhaar-analytics" element={<AadhaarDashboard />} />
          <Route path="teacher-dashboard" element={<TeacherDashboard />} />
          <Route path="infrastructure-dashboard" element={<InfrastructureDashboard />} />
          <Route path="enrolment-dashboard" element={<EnrolmentDashboard />} />
          <Route path="dropbox-dashboard" element={<DropboxDashboard />} />
          <Route path="data-entry-dashboard" element={<DataEntryDashboard />} />
          <Route path="age-enrolment-dashboard" element={<AgeEnrolmentDashboard />} />
          <Route path="ctteacher-dashboard" element={<CTTeacherDashboard />} />
          <Route path="apaar-dashboard" element={<APAARDashboard />} />
          <Route path="classrooms-toilets-dashboard" element={<ClassroomsToiletsDashboard />} />
          <Route path="school-type-bifurcation" element={<SchoolTypeBifurcationDashboard />} />
          <Route path="managementwise-bifurcation" element={<ManagementwiseBifurcationDashboard />} />
          <Route path="executive-dashboard" element={<ExecutiveDashboard />} />
          <Route path="analytics-dashboard" element={<AnalyticsDashboard />} />
          <Route
            path="user-management"
            element={
              <AdminOnly user={user}>
                <UserManagement />
              </AdminOnly>
            }
          />
          <Route path="district/:districtCode" element={<DistrictDetail />} />
          <Route path="block/:blockCode" element={<BlockDetail />} />
          <Route path="school/:udiseCode" element={<SchoolDetail />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
  );

  return useHashRouter ? (
    <HashRouter>
      <ScrollToTop />
      {routes}
    </HashRouter>
  ) : (
    <BrowserRouter basename={ROUTER_BASENAME}>
      <ScrollToTop />
      {routes}
    </BrowserRouter>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function initAuth() {
      try {
        const authUser = await ensureAuthenticated();
        if (authUser && !cancelled) setUser(authUser);
      } catch (error) {
        console.warn("Session init failed:", error?.response?.data?.detail || error.message);
        clearStoredSession();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    initAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let refreshPromise = null;

    const responseInterceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error?.config;
        const status = error?.response?.status;
        if (status !== 401 || !originalRequest || originalRequest._authRetry) {
          return Promise.reject(error);
        }

        const url = typeof originalRequest.url === "string" ? originalRequest.url : "";
        if (!url.includes("/api/") || url.includes("/api/auth/login")) {
          return Promise.reject(error);
        }

        originalRequest._authRetry = true;
        try {
          clearStoredSession();
          if (!refreshPromise) {
            refreshPromise = performAutoLogin().finally(() => {
              refreshPromise = null;
            });
          }
          const authUser = await refreshPromise;
          if (!authUser) return Promise.reject(error);

          const token = localStorage.getItem("token");
          if (token) {
            if (typeof originalRequest.headers?.set === "function") {
              originalRequest.headers.set("Authorization", `Bearer ${token}`);
            } else {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
          }
          return axios(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }
    );

    // Attach auth token (if present) + district/block/school scope (if set)
    // to all backend /api requests.
    const requestInterceptorId = axios.interceptors.request.use((config) => {
      try {
        // Allow opt-out (used by ScopeFilter when fetching unscoped dropdown values)
        if (config?.headers && config.headers["x-skip-scope"] === "true") return config;

        const url = typeof config.url === "string" ? config.url : "";
        const base = getBackendOrigin() || window.location.origin;
        const fullUrl = url.startsWith("http") ? new URL(url) : new URL(url, base);
        if (fullUrl.origin !== getBackendOrigin()) return config;
        if (!fullUrl.pathname.startsWith("/api/")) return config;

        // Ensure Authorization is present for protected endpoints (e.g. Advanced Analytics).
        // This is intentionally resilient to app refreshes / backend restarts where axios defaults
        // might not be set yet, but localStorage still has the token.
        const token = localStorage.getItem("token");
        if (token) {
          // Axios v1 may use AxiosHeaders which requires .set()/.get()
          const hasSet = typeof config?.headers?.set === "function";
          const hasGet = typeof config?.headers?.get === "function";

          if (hasSet) {
            const existing = hasGet ? config.headers.get("Authorization") : null;
            if (!existing) config.headers.set("Authorization", `Bearer ${token}`);
          } else {
            config.headers = config.headers || {};
            if (!config.headers.Authorization && !config.headers.authorization) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          }
        }

        const raw = localStorage.getItem("dashboard_scope_v1");
        if (!raw) return config;
        const scope = JSON.parse(raw);
        if (!scope || typeof scope !== "object") return config;

        config.params = { ...(config.params || {}) };
        const isExecutiveAggregate = fullUrl.pathname.startsWith("/api/executive/");
        if (!config.params.district_code && scope.districtCode) config.params.district_code = scope.districtCode;
        if (!config.params.block_code && scope.blockCode) config.params.block_code = scope.blockCode;
        if (!isExecutiveAggregate && !config.params.udise_code && scope.udiseCode) {
          config.params.udise_code = scope.udiseCode;
        }
        if (!config.params.district_name && scope.districtName) config.params.district_name = scope.districtName;
        if (!config.params.block_name && scope.blockName) config.params.block_name = scope.blockName;
        if (!isExecutiveAggregate && !config.params.school_name && scope.schoolName) {
          config.params.school_name = scope.schoolName;
        }
      } catch {
        // ignore
      }
      return config;
    });

    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(responseInterceptorId);
    };
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  if (loading) {
    return <AppBootScreen subtitle="Starting dashboard services…" />;
  }

  return (
    <div className="App">
      {IS_GITHUB_PAGES && BACKEND_URL.includes("localhost") ? (
        <div className="mx-auto max-w-5xl px-4 pt-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <span className="font-semibold">Demo mode:</span>{" "}
            this GitHub Pages site hosts only the frontend UI. To see real data, run the backend + MongoDB locally and set{" "}
            <code className="px-1 py-0.5 rounded bg-amber-100">REACT_APP_BACKEND_URL</code>.
          </div>
        </div>
      ) : null}

      {IS_GITHUB_PAGES ? (
        <AppRoutes user={user} setUser={handleLogin} onLogout={handleLogout} useHashRouter />
      ) : (
        <AppRoutes user={user} setUser={handleLogin} onLogout={handleLogout} useHashRouter={false} />
      )}
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
